import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import useCommunicationsLeaveGuard from '../../shared/useCommunicationsLeaveGuard'
import {
  ActionButton,
  Button,
  Checkbox,
  InputField,
} from '@enamelai/design-system'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import {
  CallFlowBranchKey,
  CallFlowStepType,
  ICallFlow,
  ICallFlowStep,
} from '@dataTypes/clinic.type'
import './FlowEditorStyle.css'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import WorkingHoursPanel from '../WorkingHoursPanel/WorkingHoursPanel.component'
import {
  AddToQueuePanel,
  DialNumberPanel,
  MenuIVRPanel,
  PlayAudioPanel,
  RerouteCallPanel,
  VoicemailPanel,
  AiReceptionistPanel,
  RouteSmsPanel,
  SendSmsPanel,
} from '../StepPanels'
import { MENU_IVR_ACTION_OPTIONS } from '../StepPanels/MenuIVR/useMenuIVR'
import useFlowEditor, { dialOutcomeAnchorRefKey } from './useFlowEditor'
import { useAppSelector } from '@store/hooks'
import { fetchAiReceptionistSettingsApi } from '@services/api/ai-receptionist-settings.api'
import FlowBranchFork from './FlowBranchFork.component'
import CallFlowReassignNumberModal from '../CallFlowReassignNumberModal.component'
import {
  clinicNumberEligibleForFlow,
  flowTypeLabel,
  stripSmsMarkup,
} from '@utils/communicationFlow.util'

const isUnsetOrNA = (v: string | null | undefined): boolean =>
  v == null || String(v).trim() === '' || String(v).trim() === 'N/A'

const CALL_STEP_TYPES: {
  type: CallFlowStepType
  label: string
  icon: string
}[] = [
  {
    type: 'ai_receptionist',
    label: 'AI receptionist',
    icon: 'mgc_voice_line',
  },
  { type: 'play_audio', label: 'Play audio', icon: 'mgc_play_circle_fill' },
  { type: 'menu_ivr', label: 'Menu (IVR)', icon: 'menuIVR.svg' },
  {
    type: 'dial_number',
    label: 'Fwd. number',
    icon: 'mgc_phone_outgoing_fill',
  },
  {
    type: 'add_queue',
    label: 'Add to queue',
    icon: 'phoneQueue.svg',
  },
  {
    type: 'route_call',
    label: 'Route call',
    icon: 'routeCall.svg',
  },
  {
    type: 'voicemail',
    label: 'Play voicemail',
    icon: 'voicemail.svg',
  },
  { type: 'hang_up', label: 'Hang up', icon: 'mgc_close_line' },
]

const SMS_STEP_TYPES: {
  type: CallFlowStepType
  label: string
  icon: string
}[] = [
  { type: 'route_sms', label: 'Route', icon: 'routeCall.svg' },
  { type: 'send_sms', label: 'Send SMS', icon: 'mgc_message_4_fill' },
  {
    type: 'ai_receptionist',
    label: 'AI receptionist',
    icon: 'mgc_voice_line',
  },
  {
    type: 'no_action',
    label: 'No action',
    icon: 'mgc_message_4_line mgc-no-action-icon',
  },
]

const BRANCH_ICONS: Record<CallFlowBranchKey, string> = {
  work: 'mgc_time_line',
  lunch: 'mgc_time_duration_line',
  closed: 'mgc_close_circle_line',
}

export type FlowEditorProps = {
  initialFlow: ICallFlow | null
  cancelEditor: () => void
}

const branchLabel: Record<CallFlowBranchKey, string> = {
  work: 'Work hours',
  lunch: 'Lunch break',
  closed: 'When closed',
}

export const isStepCompleteForDialOutcome = (
  s: ICallFlowStep | undefined,
): boolean => {
  if (!s) return false
  switch (s.type) {
    case 'hang_up':
    case 'no_action':
      return true
    case 'ai_receptionist':
      return true
    case 'play_audio':
      return Boolean(s.fileName?.trim() || s.audioUrl?.trim())
    case 'menu_ivr':
      return Boolean(
        s.ttsText?.trim() &&
          s.keys?.length &&
          s.keys.every((k) => Boolean(k.digit?.trim() && k.action?.trim())),
      )
    case 'add_queue':
      return Boolean(
        s.team?.trim() &&
          s.informPosition != null &&
          s.holdMusicId?.trim() &&
          s.onTimeout?.trim() &&
          s.maxWaitSec != null &&
          !Number.isNaN(s.maxWaitSec) &&
          s.maxWaitSec > 0 &&
          (s.holdMusicId !== 'custom' || Boolean(s.holdMusicCustomAudioUrl)) &&
          (s.onTimeout !== 'reroute' ||
            !(isUnsetOrNA(s.rerouteNumber) && isUnsetOrNA(s.rerouteExtension))),
      )
    case 'route_call':
    case 'reroute_call':
      return Boolean(
        s.team?.trim() &&
          s.ringType?.trim() &&
          s.ringDurationSec != null &&
          !Number.isNaN(s.ringDurationSec) &&
          s.ringDurationSec > 0 &&
          s.ifNoAnswer?.trim() &&
          (s.ifNoAnswer !== 'voicemail' || s.voicemailAllowRerecord != null) &&
          (s.ifNoAnswer !== 'voicemail' ||
            s.voicemailAllowRerecord === false ||
            (s.voicemailMaxRecordingSec != null &&
              !Number.isNaN(s.voicemailMaxRecordingSec) &&
              s.voicemailMaxRecordingSec > 0 &&
              s.includeVoicemailTranscription != null)) &&
          (s.ifNoAnswer !== 'tts' || Boolean(s.ifNoAnswerTtsText?.trim())),
      )
    case 'voicemail':
      return Boolean(
        (s.greetingFileName?.trim() || s.greetingAudioUrl?.trim()) &&
          s.maxRecordingSec != null &&
          !Number.isNaN(s.maxRecordingSec) &&
          s.maxRecordingSec > 0 &&
          s.voicemailAllowRerecord != null &&
          (s.voicemailAllowRerecord === false ||
            s.includeVoicemailTranscription != null) &&
          s.afterRecording?.trim(),
      )
    case 'dial_number':
      return Boolean(
        (!isUnsetOrNA(s.phoneNumber) || !isUnsetOrNA(s.extension)) &&
          isStepCompleteForDialOutcome(s.answeredFollowStep) &&
          isStepCompleteForDialOutcome(s.noAnswerFollowStep),
      )
    default:
      return false
  }
}

const CallFlowEditor = ({
  initialFlow,
  cancelEditor,
}: FlowEditorProps): React.ReactElement => {
  const hooks = useFlowEditor({ initialFlow, cancelEditor })
  const leaveGuard = useCommunicationsLeaveGuard(hooks.hasChanges)
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const [aiEnabled, setAiEnabled] = useState(false)

  useEffect(() => {
    const clinicId = selectedClinic?.clinic_id
    if (!clinicId) return
    fetchAiReceptionistSettingsApi(clinicId)
      .then((response) => {
        const settings = response.data.ai_receptionist_settings
        const channels = Array.isArray(settings.channels)
          ? settings.channels
          : []
        const channel =
          (hooks.callFlow.flow_type ?? 'call') === 'sms' ? 'sms' : 'call'
        setAiEnabled(settings.enabled === true && channels.includes(channel))
      })
      .catch(() => setAiEnabled(false))
  }, [selectedClinic?.clinic_id, hooks.pickerOpen, hooks.callFlow.flow_type])

  const rootNodeLabel = useMemo((): ReactNode => {
    const ids = hooks.selectedNumbers.map((n) => n.clinic_number_id!)
    if (ids.length === 0) {
      return 'Select number(s)'
    }
    const lines = ids
      .map((id) => hooks.clinicNumbers.find((n) => n.clinic_number_id === id))
      .filter(Boolean)
    if (lines.length === 0) {
      return ids.length === 1 ? '1 number' : `${ids.length} numbers`
    }
    const formatted = lines.map((n) => formatPhoneNumber(n!.phone_number))
    if (formatted.length === 1) {
      return formatted[0] ?? 'Select number(s)'
    }
    return (
      <>
        {formatted[0] ?? ''}
        <span className="number-count">+{formatted.length - 1}</span>
      </>
    )
  }, [hooks.selectedNumbers, hooks.clinicNumbers])

  const stepLabel = (
    s: ICallFlowStep,
    branchKey: CallFlowBranchKey,
    nestedDial?: { parentStepId: string; outcome: 'answered' | 'no_answer' },
  ): ReactNode => {
    const onClick = (): void => {
      const sameEditing =
        hooks.editingStep?.branch === branchKey &&
        hooks.editingStep?.stepId === s.id &&
        (nestedDial
          ? hooks.editingStep.dialParentStepId === nestedDial.parentStepId &&
            hooks.editingStep.dialOutcome === nestedDial.outcome
          : hooks.editingStep.dialParentStepId == null &&
            hooks.editingStep.dialOutcome == null)
      if (sameEditing) {
        hooks.closeStepPanel()
        return
      }
      hooks.setEditingStep(
        nestedDial
          ? {
              branch: branchKey,
              stepId: s.id,
              dialParentStepId: nestedDial.parentStepId,
              dialOutcome: nestedDial.outcome,
            }
          : { branch: branchKey, stepId: s.id },
      )
    }

    let isActive: boolean = false
    if (s.type === 'play_audio') {
      isActive = (s as { fileName?: string }).fileName ? true : false
    } else if (s.type === 'menu_ivr') {
      const _s = s as {
        ttsText?: string
        keys?: {
          digit: string
          action: string
          meta?: Record<string, string>
        }[]
      }
      isActive = _s.ttsText && _s.keys && _s.keys.length > 0 ? true : false
    } else if (s.type === 'dial_number') {
      const _s = s
      isActive = Boolean(
        !isUnsetOrNA(_s.phoneNumber) || !isUnsetOrNA(_s.extension),
      )
    } else if (s.type === 'add_queue') {
      const q = s
      const rerouteOk =
        q.onTimeout !== 'reroute' ||
        !(isUnsetOrNA(q.rerouteNumber) && isUnsetOrNA(q.rerouteExtension))
      const customHoldOk =
        q.holdMusicId !== 'custom' || Boolean(q.holdMusicCustomAudioUrl?.trim())
      isActive = Boolean(
        q.team?.trim() &&
          q.informPosition != null &&
          q.holdMusicId?.trim() &&
          q.onTimeout?.trim() &&
          q.maxWaitSec != null &&
          !Number.isNaN(q.maxWaitSec) &&
          q.maxWaitSec > 0 &&
          customHoldOk &&
          rerouteOk,
      )
    } else if (s.type === 'route_call' || s.type === 'reroute_call') {
      const r = s
      const base =
        Boolean(
          r.team?.trim() &&
            r.ringType?.trim() &&
            r.ringDurationSec != null &&
            !Number.isNaN(r.ringDurationSec) &&
            r.ringDurationSec > 0 &&
            r.ifNoAnswer?.trim() &&
            (r.ifNoAnswer === 'voicemail'
              ? r.voicemailAllowRerecord != null
              : true),
        ) &&
        (r.ifNoAnswer !== 'voicemail' ||
          r.voicemailAllowRerecord === false ||
          (r.voicemailMaxRecordingSec != null &&
            !Number.isNaN(r.voicemailMaxRecordingSec) &&
            r.voicemailMaxRecordingSec > 0 &&
            r.includeVoicemailTranscription != null)) &&
        (r.ifNoAnswer !== 'tts' || Boolean(r.ifNoAnswerTtsText?.trim()))
      isActive = base
    } else if (s.type === 'ai_receptionist') {
      isActive = true
    } else if (s.type === 'send_sms') {
      isActive = Boolean(stripSmsMarkup(s.message))
    } else if (s.type === 'no_action') {
      isActive = true
    } else if (s.type === 'route_sms') {
      isActive = Boolean(
        s.team?.trim() &&
          s.timeoutMinutes != null &&
          !Number.isNaN(s.timeoutMinutes) &&
          s.timeoutMinutes > 0 &&
          (s.ifNoReply === 'ai_receptionist' ||
            (s.ifNoReply === 'send_sms' && stripSmsMarkup(s.message))),
      )
    } else if (s.type === 'voicemail') {
      const v = s
      const hasGreeting = Boolean(
        v.greetingFileName?.trim() || v.greetingAudioUrl?.trim(),
      )
      isActive = Boolean(
        hasGreeting &&
          v.maxRecordingSec != null &&
          !Number.isNaN(v.maxRecordingSec) &&
          v.maxRecordingSec > 0 &&
          v.voicemailAllowRerecord != null &&
          (v.voicemailAllowRerecord === false ||
            v.includeVoicemailTranscription != null) &&
          v.afterRecording?.trim(),
      )
    }

    const labels: Record<string, ReactNode> = {
      ai_receptionist: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={<i className="mgc-icon mgc_voice_line" />}
          onClick={onClick}
        >
          AI receptionist
          <i
            role="button"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      play_audio: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={<i className="mgc-icon mgc_play_circle_fill" />}
          onClick={onClick}
        >
          Play audio
          <i
            role="button"
            tabIndex={0}
            aria-label="Remove step"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      menu_ivr: (
        <>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
            leftIcon={
              <img
                src="/icons/menuIVR.svg"
                alt="menu-ivr"
                height={16}
                width={16}
              />
            }
            onClick={onClick}
          >
            Menu (IVR)
            <i
              role="button"
              tabIndex={0}
              aria-label="Remove step"
              className="mgc-icon mgc_close_circle_fill scp-remove-step"
              onClick={(e) => {
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  hooks.removeStepFromBranch(branchKey, s.id)
                }
              }}
            />
          </Button>
          {(s as { keys: { digit: string; action: string }[] }).keys?.map(
            (k) => (
              <>
                <span
                  key={k.digit}
                  className="scp-flow-branch-pill scp-menu-ivr-key"
                >
                  {k.digit}:{' '}
                  {MENU_IVR_ACTION_OPTIONS.find((o) => o.value === k.action)
                    ?.label ?? k.action}
                </span>
              </>
            ),
          )}
        </>
      ),
      dial_number: (
        <div className="scp-dial-number-flow">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={`scp-flow-node-box scp-dial-number-node ${isActive ? 'active' : ''}`}
            leftIcon={<i className="mgc-icon mgc_phone_outgoing_fill" />}
            onClick={onClick}
          >
            Fwd. call
            <i
              role="button"
              tabIndex={0}
              aria-label="Remove step"
              className="mgc-icon mgc_close_circle_fill scp-remove-step"
              onClick={(e) => {
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  hooks.removeStepFromBranch(branchKey, s.id)
                }
              }}
            />
          </Button>
          {isActive && (
            <div className="scp-dial-number-fork-block">
              <FlowBranchFork
                branches={2}
                className="scp-flow-branch-fork scp-flow-branch-fork--dial"
              />
              <div
                className="scp-dial-number-outcomes"
                role="group"
                aria-label="Dial outcomes"
              >
                <div className="scp-dial-outcome-col">
                  <div className="scp-dial-outcome-pill scp-dial-outcome-pill--answered">
                    <i className="mgc-icon mgc_phone_fill scp-dial-outcome-ic--answered" />
                    <span>Answered</span>
                  </div>
                  <img
                    className="scp-dial-number-v-arrow"
                    src="/icons/verticalArrow.svg"
                    alt=""
                  />
                  {s.type === 'dial_number' && s.answeredFollowStep ? (
                    stepLabel(s.answeredFollowStep, branchKey, {
                      parentStepId: s.id,
                      outcome: 'answered',
                    })
                  ) : (
                    <span
                      ref={(el) => {
                        hooks.dialOutcomeBtnRefs.current[
                          dialOutcomeAnchorRefKey(branchKey, s.id, 'answered')
                        ] = el
                      }}
                      className="scp-flow-add-step-anchor"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="scp-flow-node-box scp-flow-add-btn scp-dial-add-step scp-dial-add-step--tinted"
                        leftIcon={
                          <i className="mgc-icon mgc_add_circle_line" />
                        }
                        onClick={() =>
                          hooks.onAddDialOutcomeStep(
                            branchKey,
                            s.id,
                            'answered',
                          )
                        }
                      >
                        Add step
                      </Button>
                    </span>
                  )}
                </div>
                <div className="scp-dial-outcome-col">
                  <div className="scp-dial-outcome-pill scp-dial-outcome-pill--no-answer">
                    <i className="mgc-icon mgc_phone_off_fill scp-dial-outcome-ic--no-answer" />
                    <span>No answer</span>
                  </div>
                  <img
                    className="scp-dial-number-v-arrow"
                    src="/icons/verticalArrow.svg"
                    alt=""
                  />
                  {s.type === 'dial_number' && s.noAnswerFollowStep ? (
                    stepLabel(s.noAnswerFollowStep, branchKey, {
                      parentStepId: s.id,
                      outcome: 'no_answer',
                    })
                  ) : (
                    <span
                      ref={(el) => {
                        hooks.dialOutcomeBtnRefs.current[
                          dialOutcomeAnchorRefKey(branchKey, s.id, 'no_answer')
                        ] = el
                      }}
                      className="scp-flow-add-step-anchor"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="scp-flow-node-box scp-flow-add-btn scp-dial-add-step scp-dial-add-step--plain"
                        leftIcon={
                          <i className="mgc-icon mgc_add_circle_line" />
                        }
                        onClick={() =>
                          hooks.onAddDialOutcomeStep(
                            branchKey,
                            s.id,
                            'no_answer',
                          )
                        }
                      >
                        Add step
                      </Button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ),
      add_queue: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box scp-dial-number-node ${isActive ? 'active' : ''}`}
          leftIcon={
            <img
              src="/icons/phoneQueue.svg"
              alt="add-to-queue"
              height={16}
              width={16}
            />
          }
          onClick={onClick}
        >
          Add to queue
          <i
            role="button"
            tabIndex={0}
            aria-label="Remove step"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      route_call: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={
            <img
              src="/icons/routeCall.svg"
              alt="route-call"
              height={16}
              width={16}
            />
          }
          onClick={onClick}
        >
          Route call
          <i
            role="button"
            tabIndex={0}
            aria-label="Remove step"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      voicemail: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={
            <img
              src="/icons/voicemail.svg"
              alt="voicemail"
              height={16}
              width={16}
            />
          }
          onClick={onClick}
        >
          Voicemail
          <i
            role="button"
            tabIndex={0}
            aria-label="Remove step"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      reroute_call: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={
            <img
              src="/icons/routeCall.svg"
              alt="route-call"
              height={16}
              width={16}
            />
          }
          onClick={onClick}
        >
          Route call
          <i
            role="button"
            tabIndex={0}
            aria-label="Remove step"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                hooks.removeStepFromBranch(branchKey, s.id)
              }
            }}
          />
        </Button>
      ),
      send_sms: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={<i className="mgc-icon mgc_message_4_fill" />}
          onClick={onClick}
        >
          Send SMS
          <i
            role="button"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
          />
        </Button>
      ),
      route_sms: (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`scp-flow-node-box ${isActive ? 'active' : ''}`}
          leftIcon={
            <img src="/icons/routeCall.svg" alt="" height={16} width={16} />
          }
          onClick={onClick}
        >
          Route
          <i
            role="button"
            className="mgc-icon mgc_close_circle_fill scp-remove-step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
          />
        </Button>
      ),
      no_action: (
        <span className="scp-flow-red-pill">
          <i className="mgc_message_4_fill mgc-no-action-icon" />
          No action
          <button
            type="button"
            className="unstyled-button mgc-icon mgc_close_circle_fill scp-remove-step"
            aria-label="Remove step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
          />
        </span>
      ),
      hang_up: (
        <span className="scp-flow-red-pill">
          <i className="mgc_phone_block_fill mgc-hang-up-icon" />
          Hang up
          <button
            type="button"
            className="unstyled-button mgc-icon mgc_close_circle_fill scp-remove-step"
            aria-label="Remove step"
            onClick={(e) => {
              e.stopPropagation()
              hooks.removeStepFromBranch(branchKey, s.id)
            }}
          />
        </span>
      ),
    }
    return labels[s.type] ?? s.type
  }

  const renderStepConfigPanel = (): React.ReactElement | null => {
    const ed = hooks.editingStep
    if (!ed) return null
    let step: ICallFlowStep | undefined
    if (ed.dialParentStepId && ed.dialOutcome) {
      const parent = hooks.branches[ed.branch].steps.find(
        (st) => st.id === ed.dialParentStepId,
      )
      if (!parent || parent.type !== 'dial_number') return null
      step =
        ed.dialOutcome === 'answered'
          ? parent.answeredFollowStep
          : parent.noAnswerFollowStep
      if (!step || step.id !== ed.stepId) return null
    } else {
      step = hooks.branches[ed.branch].steps.find((st) => st.id === ed.stepId)
    }
    if (!step) return null
    const bl = branchLabel[ed.branch]
    const close = hooks.closeStepPanel
    switch (step.type) {
      case 'play_audio':
        return (
          <PlayAudioPanel
            step={step}
            branchLabel={bl}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
            onPendingFile={(f) =>
              hooks.registerPendingAudioFile(step?.id ?? '', f)
            }
          />
        )
      case 'menu_ivr':
        return (
          <MenuIVRPanel
            step={step}
            branchLabel={bl}
            internalExtensions={hooks.internalExtensions}
            aiEnabled={aiEnabled}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      case 'dial_number':
        return (
          <DialNumberPanel
            step={step}
            branchLabel={bl}
            clinicNumbers={hooks.clinicNumbers}
            internalExtensions={hooks.internalExtensions}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      case 'add_queue':
        return (
          <AddToQueuePanel
            step={step}
            branchLabel={bl}
            clinicNumbers={hooks.clinicNumbers}
            internalExtensions={hooks.internalExtensions}
            aiEnabled={aiEnabled}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
            onPendingFile={(f) =>
              hooks.registerPendingAudioFile(step?.id ?? '', f)
            }
          />
        )
      case 'route_call':
      case 'reroute_call':
        return (
          <RerouteCallPanel
            step={step}
            branchLabel={bl}
            aiEnabled={aiEnabled}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      case 'voicemail':
        return (
          <VoicemailPanel
            step={step}
            branchLabel={bl}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
            onPendingFile={(f) =>
              hooks.registerPendingAudioFile(step?.id ?? '', f)
            }
          />
        )
      case 'hang_up':
      case 'no_action':
        return null
      case 'ai_receptionist':
        return (
          <AiReceptionistPanel
            step={step}
            branchLabel={bl}
            channel={hooks.callFlow.flow_type === 'sms' ? 'sms' : 'call'}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      case 'send_sms':
        return (
          <SendSmsPanel
            step={step}
            branchLabel={bl}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      case 'route_sms':
        return (
          <RouteSmsPanel
            step={step}
            branchLabel={bl}
            aiEnabled={aiEnabled}
            onChange={(p) => hooks.updateStep(ed.branch, ed.stepId, p)}
            onCancel={close}
            onDone={close}
          />
        )
      default: {
        const _exhaustive: never = step
        return _exhaustive
      }
    }
  }

  useEffect(() => {
    hooks.setHeaderTitle(
      <div className="settings-title-with-back">
        {' '}
        <button
          type="button"
          className="unstyled-button stwb-button"
          onClick={() => leaveGuard.requestLeave(cancelEditor)}
        >
          <span className="stwb-back-icon mgc_left_line" />
          <span className="stwb-text">
            Communication flow<span>/</span>
          </span>
        </button>
        <span>
          {' '}
          {initialFlow?.call_flow_id
            ? 'Edit flow'
            : hooks.callFlow.flow_type === 'sms'
              ? 'SMS flow'
              : 'Call flow'}
        </span>
      </div>,
    )
    hooks.setHeaderButtons(
      <>
        <Button
          size="sm"
          type="button"
          variant="secondary"
          disabled={!hooks.hasChanges}
          onClick={() => leaveGuard.requestLeave(() => hooks.resetDraft())}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          variant="primary"
          disabled={!hooks.hasChanges}
          onClick={() => void hooks.saveFlow()}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      hooks.setHeaderTitle(null)
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.hasChanges,
    initialFlow,
    cancelEditor,
    leaveGuard.requestLeave,
    hooks.resetDraft,
    hooks.saveFlow,
    hooks.setHeaderButtons,
    hooks.setHeaderTitle,
  ])

  return (
    <>
      {leaveGuard.modal}
      <div className="scp-call-flow-editor-wrapper">
        <div className="scp-call-flow-editor">
          <div className="scp-call-flow-name">
            {!hooks.editName ? (
              <>
                {hooks.callFlow.name}
                <button
                  type="button"
                  className="ds-section-card__icon-button"
                  onClick={() => hooks.setEditName(true)}
                >
                  <EditIcon className="edit-icon-button" aria-hidden="true" />
                </button>
              </>
            ) : (
              <>
                <InputField
                  className="scp-inline-field"
                  variant="text"
                  label=""
                  aria-label="Flow name"
                  clearable={false}
                  value={hooks.callFlow.name}
                  onChange={(event) =>
                    hooks.setCallFlow({
                      ...hooks.callFlow,
                      name: event.target.value,
                    })
                  }
                />
                <ActionButton
                  size="sm"
                  tone="neutral"
                  variant="outline"
                  label="Cancel"
                  icon={
                    <CloseIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  }
                  onClick={hooks.cancelEditName}
                />
                <ActionButton
                  size="sm"
                  tone="primary"
                  variant="outline"
                  label="Save"
                  icon={
                    <CheckmarkIcon
                      className="ds-action-button__svg save-icon-button"
                      aria-hidden="true"
                    />
                  }
                  onClick={hooks.saveName}
                />
              </>
            )}
          </div>

          <div className="scp-call-flow-canvas" ref={hooks.diagramWrapRef}>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={`scp-flow-node-box${hooks.numberListOpen ? ' scp-flow-node-active' : ''}`}
              rightIcon={<i className="mgc-icon mgc_down_small_fill" />}
              onClick={() =>
                hooks.setNumberListOpen((o) => {
                  if (!o) hooks.closeStepPanel()
                  return !o
                })
              }
            >
              {rootNodeLabel}{' '}
            </Button>
            <img src="/icons/verticalArrow.svg" alt="vertical-arrow" />

            <div className="scp-flow-node-box node-text-main">
              <i
                className={`mgc-icon ${
                  hooks.callFlow.flow_type === 'sms'
                    ? 'mgc_message_4_fill'
                    : 'mgc_phone_fill'
                }`}
              />
              {hooks.callFlow.flow_type === 'sms'
                ? 'Message received'
                : 'Phone ringing'}
            </div>
            {hooks.showCallFlowBranches && (
              <div className="scp-flow-branch-block">
                <FlowBranchFork branches={3} className="scp-flow-branch-fork" />
                <div className="scp-flow-branches">
                  {(['work', 'lunch', 'closed'] as CallFlowBranchKey[]).map(
                    (key) => (
                      <div key={key} className="scp-flow-branch-col">
                        <div className="scp-flow-branch-pill">
                          <i className={`mgc-icon ${BRANCH_ICONS[key]}`} />
                          {branchLabel[key]}
                        </div>
                        <img
                          src="/icons/verticalArrow.svg"
                          alt="vertical-arrow"
                        />
                        {hooks.branches[key].steps.length > 0 ? (
                          hooks.branches[key].steps.map((step) => (
                            <div
                              key={step.id}
                              className={`scp-flow-step-card node-text-main${
                                step.type === 'dial_number'
                                  ? ' scp-flow-step-card--dial-number'
                                  : ''
                              }`}
                            >
                              {stepLabel(step, key)}
                            </div>
                          ))
                        ) : (
                          <span
                            ref={(el) => {
                              hooks.addStepBtnRefs.current[key] = el
                            }}
                            className="scp-flow-add-step-anchor"
                          >
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="scp-flow-node-box scp-flow-add-btn"
                              leftIcon={
                                <i className="mgc-icon mgc_add_circle_line" />
                              }
                              onClick={() => hooks.onAddStep(key)}
                            >
                              Add step
                            </Button>
                          </span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
            {/* TODO: Add back in when we have a plan on call flow AI */}
            {/* {hooks.showCallFlowBranches &&
              (callFlowAiOpen ? (
                <div className="scp-call-flow-ai-floater">
                  <button
                    type="button"
                    className="scp-call-flow-ai-floater__close"
                    aria-label="Close AI assistant"
                    onClick={() => setCallFlowAiOpen(false)}
                  >
                    <i className="mgc_close_line" />
                  </button>
                  <TextEditor
                    className="scp-call-flow-ai-text-editor"
                    rteId="call-flow-ai-rte"
                    editorInput={callFlowAiInput}
                    setEditorInput={setCallFlowAiInput}
                    isAiEnabled={callFlowAiMode}
                    setIsAiEnabled={setCallFlowAiMode}
                    handleSend={handleCallFlowAiSend}
                    hideToolbar={false}
                    isEmojiEnabled={true}
                    isLinkEnabled={false}
                    persistAiMode
                    hideAiQuickActions
                    showAiToolbarToggle={false}
                    showClearButton={false}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  className="scp-call-flow-ai-reopen"
                  onClick={() => setCallFlowAiOpen(true)}
                >
                  <i className="mgc_magic_3_fill mgc-icon" aria-hidden />
                  Ask AI
                </button>
              ))} */}
          </div>

          {hooks.numberListOpen ? (
            <div
              ref={hooks.numberListFlyoutRef}
              className="scp-call-flow-number-list"
              role="dialog"
              aria-label="Select numbers for this flow"
            >
              <div className="number-list-items">
                {hooks.clinicNumbers
                  .filter((number) => {
                    const flowType = hooks.callFlow.flow_type ?? 'call'
                    const selected = hooks.selectedNumbers.some(
                      (item) =>
                        item.clinic_number_id === number.clinic_number_id,
                    )
                    return (
                      selected || clinicNumberEligibleForFlow(number, flowType)
                    )
                  })
                  .map((number) => {
                    const flowType = hooks.callFlow.flow_type ?? 'call'
                    const ineligible = !clinicNumberEligibleForFlow(
                      number,
                      flowType,
                    )
                    return (
                      <div
                        className="number-list-item"
                        key={number.clinic_number_id}
                      >
                        <Checkbox
                          key={`${number.clinic_number_id}-${hooks.numberListCheckboxRemountKey[number.clinic_number_id!] ?? 0}`}
                          aria-label={formatPhoneNumber(number.phone_number)}
                          checked={hooks.selectedNumbers.some(
                            (num) =>
                              num.clinic_number_id === number.clinic_number_id,
                          )}
                          onChange={(event) =>
                            hooks.handleSelectNumber(
                              { checked: event.target.checked },
                              number,
                            )
                          }
                        />
                        <button
                          type="button"
                          className="unstyled-button scp-number-label"
                          onClick={() => {
                            if (
                              !hooks.selectedNumbers.some(
                                (num) =>
                                  num.clinic_number_id ===
                                  number.clinic_number_id,
                              )
                            ) {
                              hooks.handleSelectNumber(
                                { checked: true },
                                number,
                              )
                            } else {
                              hooks.closeStepPanel()
                              hooks.setActiveNumber(number)
                            }
                          }}
                        >
                          {formatPhoneNumber(number.phone_number)}
                          {ineligible ? (
                            <span className="scp-number-ineligible">
                              This number is no longer active for this flow
                              type.
                            </span>
                          ) : null}
                        </button>
                      </div>
                    )
                  })}
              </div>
            </div>
          ) : null}
          {hooks.pickerOpen && hooks.pickerPos ? (
            <div
              className={`scp-call-flow-step-picker${
                hooks.pickerPos.placement === 'above'
                  ? ' scp-call-flow-step-picker--above'
                  : ''
              }`}
              style={{
                top: hooks.pickerPos.top,
                left: hooks.pickerPos.left,
              }}
              role="dialog"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="scp-call-flow-step-picker-scroll">
                {(hooks.callFlow.flow_type === 'sms'
                  ? SMS_STEP_TYPES
                  : CALL_STEP_TYPES
                )
                  .filter((st) => st.type !== 'ai_receptionist' || aiEnabled)
                  .map((st) => {
                    const dialPicker = hooks.pickerDialContext != null
                    const fwdDisabled = dialPicker && st.type === 'dial_number'
                    return (
                      <Button
                        key={st.type}
                        type="button"
                        size="sm"
                        variant="ghost"
                        className={`scp-call-flow-step-tile${fwdDisabled ? ' scp-call-flow-step-tile--disabled' : ''}`}
                        disabled={fwdDisabled}
                        onClick={() => {
                          if (fwdDisabled) return
                          hooks.addStepToBranch(st.type)
                        }}
                      >
                        {st.icon.endsWith('.svg') ? (
                          <img
                            src={`/icons/${st.icon}`}
                            alt={st.label}
                            width="40px"
                            height="40px"
                          />
                        ) : (
                          <i
                            className={`mgc-icon scp-step-tile-ic ${st.icon}`}
                          />
                        )}
                        {st.label}
                      </Button>
                    )
                  })}
              </div>
            </div>
          ) : null}
        </div>
        {hooks.editingStep ? (
          renderStepConfigPanel()
        ) : hooks.activeNumber ? (
          <WorkingHoursPanel
            updateActiveNumber={hooks.updateActiveNumber}
            onClinicNumbersSaved={hooks.onClinicNumbersSaved}
            phoneLabel={hooks.workingHoursPhoneLabel || '—'}
            activeNumber={hooks.activeNumber}
            selectedNumbers={hooks.selectedNumbers}
            clinicNumbers={hooks.clinicNumbers}
            workingHoursDataDirty={hooks.workingHoursDataDirty}
            onNext={hooks.goToNextWorkingHoursNumber}
            onCancel={hooks.cancelWorkingHours}
          />
        ) : null}
      </div>
      <CallFlowReassignNumberModal
        show={hooks.numberReassignConfirm !== null}
        phoneNumberLabel={
          hooks.numberReassignConfirm
            ? formatPhoneNumber(hooks.numberReassignConfirm.number.phone_number)
            : ''
        }
        otherFlowName={hooks.numberReassignConfirm?.otherFlowName ?? ''}
        flowTypeLabel={flowTypeLabel(
          hooks.callFlow.flow_type ?? 'call',
        ).toLowerCase()}
        onCancel={hooks.cancelNumberReassign}
        onConfirm={() => hooks.confirmNumberReassign()}
      />
    </>
  )
}

export default CallFlowEditor
